/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {KeyValue} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, Observable, of, Subscription} from 'rxjs';
import {catchError, debounceTime, filter, finalize, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import mapLodash from 'lodash-es/map';

import * as moment from 'moment';

import {SdcStatusDTO} from '@gp/shared/sdconnect/sdconnect.model';
import {ToastrService} from 'ngx-toastr';
import {OngoingPayrollsService} from '../../../+payrolls';
import {CalculationType} from '../../../+payrolls/payrolls.model';
import {DATE_MAX, ProfileMode} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {PayPeriodContractChange} from '../../../inbox/shared/communications.interface';
import {CommunicationsService} from '../../../inbox/shared/communications.service';
import {EmpContractAttributes, ReportDefinition, ReportsFormData} from '../../../models/models.interfaces';
import {OnboardingStatus} from '../../../onboarding/onboarding.model';
import {ApiService, Data} from '../../api';
import {RolesService} from '../../authentication/roles/roles.service';
import {Contract, Employee, PayPeriodContract, PayPeriodContractAction} from '../../employees/employees.model';
import {EmployeesService} from '../../employees/employees.service';
import {PayPeriodContractUtilsService} from '../../employees/utils/pay-period-contract-utils.service';
import {FormDescriptorService} from '../../form-generator/form-descriptor.service';
import {ModalService} from '../../modal';
import {UserService} from '../../user';
import {isPresent} from '../../utils/lang';
import {getInitials} from '../../utils/strings';
import {BpmnWorkflow, FormModel, FormOutcome, ProcessDefinition} from '../../workflow/workflow-v2.interface';
import {getWorkflowName, REQUEST_TITLE_TRANSLATE_PREFIX, WorkflowV2Service} from '../../workflow/workflow-v2.service';

@Component({
  selector: 'gp-employee-menu',
  templateUrl: './employee-menu.component.html',
  styleUrls: ['employee-menu.component.scss'],
  providers: [FormDescriptorService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeeMenuComponent extends Destroyable implements OnChanges, OnInit, AfterViewInit, OnDestroy {

  public readonly REQUEST_TITLE_TRANSLATE_PREFIX = REQUEST_TITLE_TRANSLATE_PREFIX;

  @Input() public menuItems: any = {};
  @Input() public contractId: number;
  @Input() public cliRegId: number;
  @Input() public employeeId: number;
  @Input() public readOnly: boolean;
  @Input() public hiddenItems: any = {};
  @Input() public contract: Contract = {};
  @Input() public sdcStatus: SdcStatusDTO = {};
  @Input() public employee: Employee;
  @Input() public contracts: Array<Data<EmpContractAttributes>> = [];

  @Output() public updatedEmployee: EventEmitter<Employee> = new EventEmitter();
  @Output() public payrollStatusChanged: EventEmitter<any> = new EventEmitter();
  @Output() public contractUpdated: EventEmitter<Contract> = new EventEmitter();

  // Launch payroll variables
  public isForceRetroactivity = false;
  public isDebuggingMode = false;
  public retroactivityDate: string;
  public massActionCount: number;

  public managerOrganizations: any;

  private payrollStatus: string;
  private menuItems$: Subscription;
  private menuItemsObservable$: Observable<any>;

  public reportDefinitions: Array<ReportDefinition>;
  public selectedReportDefinition: ReportDefinition;
  public hiddenValuesForReportDefinition = new Map();
  public availableReportsType = ['EMPLOYEE_COMMUNICATION', 'EMPLOYEE_ASK_ZONE'];

  public payPeriodContracts: PayPeriodContract[] = [];
  public payTypesMap = new Map<string, PayPeriodContract[]>();
  public triggerSortByTranslationPipe = true;
  public worstPayPeriodContract: PayPeriodContract;

  public actionLaunch: PayPeriodContractAction = PayPeriodContractAction.LAUNCH;
  public actionDone: PayPeriodContractAction = PayPeriodContractAction.DONE;
  public actionFix: PayPeriodContractAction = PayPeriodContractAction.FIX;
  public actionUnfreeze: PayPeriodContractAction = PayPeriodContractAction.UNFREEZE;
  public actionLastDone: PayPeriodContractAction = PayPeriodContractAction.LAST_DONE;

  public currentPayPeriodContractId: number;

  public selectedWorkflow: ProcessDefinition;
  public selectedWorkflowName: string;
  // TODO Fix type
  public startVariables: any;
  public workflowFormModel: FormModel;
  public displayHawkTestModal: boolean;
  private workflowSubject = new BehaviorSubject<string>(null);
  private onCloseSubject = new BehaviorSubject<FormOutcome>(null);

  @ViewChildren('items')
  private menuItemsQueryList: QueryList<ElementRef>;

  private menuItemsElements: HTMLLIElement[] = [];
  private reportDefinitionsWrapper: ReportsFormData;

  public forceRefreshed: boolean;

  public isExtendWorkFlowAvailable = false;
  public isRevertTerminationAvailable = false;
  public newOnboardingEnabled: boolean;
  public isSendingOnboarding = false;

  constructor(public api: ApiService,
              private employeesService: EmployeesService,
              private payrollsService: OngoingPayrollsService,
              private cd: ChangeDetectorRef,
              private router: Router,
              private modalService: ModalService,
              private rolesService: RolesService,
              private translate: TranslateService,
              private communicationsService: CommunicationsService,
              public payPeriodContractsUtils: PayPeriodContractUtilsService,
              private toastrService: ToastrService,
              private workflowV2Service: WorkflowV2Service,
              private userService: UserService,
              private formDescriptorService: FormDescriptorService) {
    super();
    this.newOnboardingEnabled = localStorage.getItem('gp_new_onboarding_enabled') === 'true';
    this.displayHawkTestModal = localStorage.getItem('gp_hawk_test_modal_enabled') === 'true';
  }

  public ngOnInit(): void {
    this.employeesService.editListEmployee
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.fetchManageList();
      });

    this.fetchManageList();

    this.translate.onLangChange
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe;
      });

    this.payrollsService.isPayrollLaunched
      .pipe(
        takeUntil(this.componentDestroyed$))
      .subscribe((payPeriodContractId: number) => {
        if (payPeriodContractId) {
          this.openLaunchPayrollModal(payPeriodContractId);
        }
      });

    this.communicationsService.payPeriodContractChange
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(5000))
      .subscribe((payPeriodContractChanges: PayPeriodContractChange[]) => {
        if (isPresent(this.cliRegId) && isPresent(this.contractId)
          && mapLodash(payPeriodContractChanges, 'contractId').indexOf(this.contractId) > -1) {
          this.getMenuItems();
        }
      });
    this.communicationsService.calculationDone
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(2500))
      .subscribe(() => {
        if (isPresent(this.cliRegId) && isPresent(this.contractId)) {
          this.getMenuItems();
        }
      });

    this.userService.getUsersByCliEmp(this.employee.id)
      .pipe(
        filter(response => response.body && response.body[0]),
        map(response => response.body[0].id),
        tap(employeeCliUserId => this.startVariables = {
          employeeCliEmpId: this.employee.id,
          employeeFullName: this.employee.fullName,
          employeeCliUserId
        }),
        takeUntil(this.componentDestroyed$))
      .subscribe();

    this.workflowSubject.asObservable()
      .pipe(
        filter(Boolean),
        switchMap((workflowKey: string) => this.workflowV2Service.getLatestProcessDefinitionByKey(workflowKey)),
        tap(processDefinition => {
          this.selectedWorkflow = processDefinition;
          this.selectedWorkflowName = getWorkflowName(this.selectedWorkflow.key);
        }),
        switchMap(() =>
          this.workflowV2Service.startProcessInstance(this.selectedWorkflow.key, this.startVariables)
            .pipe(tap(() => this.toastrService.success(this.translate.instant('WORKFLOWS.workflow_started'))))),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.onCloseSubject.pipe(
      filter((formOutcome) => formOutcome !== null),
      switchMap((formOutcome) =>
        this.workflowV2Service.startProcessInstanceWithForm(
          this.selectedWorkflow.key,
          this.workflowFormModel,
          formOutcome,
          this.startVariables)
          .pipe(tap(() => {
            this.toastrService.success(this.translate.instant('WORKFLOWS.workflow_started'));
            this.closeModal();
          }))),
      takeUntil(this.componentDestroyed$)).subscribe();
  }

  public closeModal(): void {
    this.modalService.modalClosed.emit('workflowFormModal');
    this.cd.markForCheck();
  }

  public ngAfterViewInit() {
    this.menuItemsQueryList.changes.pipe(
      startWith(this.menuItemsQueryList),
      takeUntil(this.componentDestroyed$)
    )
      .subscribe((value: QueryList<ElementRef>) => {
        if (value.first) {
          this.menuItemsElements = value.first.nativeElement.children;
        }
      });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes?.cliRegId || changes?.contractId) {
      this.menuItems = {};
      this.payrollStatus = null;
      if (isPresent(this.cliRegId) && isPresent(this.contractId)) {
        this.getMenuItems();
      }
    }
    this.isRevertTerminationAvailable = this.sdcStatus?.personMasterDataSyncOnSDC3Activated && this.isTerminatedContract();
  }

  public openRevertTerminationModal(): void {
    if (!this.isRevertTerminationAvailable) {
      return;
    }
    this.menuItems.opened = false;
    this.modalService.confirm({
      title: 'FORMS.confirm',
      'text': 'DIALOG.revert_termination_contract?'
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.employeesService.revertTermination(this.contractId)
          .pipe(tap((updatedContract: Contract) => {
            this.toastrService.success(this.translate.instant('CONTRACTS.termination_reverted_success'));
            this.contractUpdated.emit(updatedContract);
          })).subscribe();
      }
    });
  }

  public onCloseWorkflowForm(formOutcome?: FormOutcome): void {
    this.onCloseSubject.next(formOutcome);
  }

  public sendOnboardingV2() {
    if (this.isSendingOnboarding) {
      return;
    }
    if (this.isOnboardingInProgress()) {
      this.toastrService.error(this.translate.instant('Onboarding.employee_already_onboarded'));
      return;
    }
    const employee = this.employee;
    this.isSendingOnboarding = true;
    const sendingToast = this.toastrService.info(this.translate.instant('ON_BOARDING.sending'), undefined, {
      disableTimeOut: true,
      tapToDismiss: false,
      closeButton: false
    });
    this.cd.markForCheck();
    this.menuItems.opened = false;
    this.workflowV2Service.startProcessInstance(`new_onboarding_cliReg-${this.cliRegId}`, {
      request: {
        contractId: this.contractId,
        cliEmpId: this.employeeId,
      },
      cliRegId: this.cliRegId,
    }, false, ProfileMode.CLASSIC).pipe(
      tap(() => {
        this.toastrService.success(this.translate.instant('ON_BOARDING.sent'));
        this.updateOnboardingStatus(employee, OnboardingStatus.WAITING_SUBMIT_FROM_EMPLOYEE);
      }),
      catchError(() => {
        this.toastrService.error(this.translate.instant('ON_BOARDING.send_error'));
        return of(null);
      }),
      finalize(() => {
        this.isSendingOnboarding = false;
        if (sendingToast) {
          this.toastrService.clear(sendingToast.toastId);
        }
        this.cd.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private updateOnboardingStatus(employee: Employee, status: OnboardingStatus): void {
    if (!employee?.attributes) {
      return;
    }
    employee.attributes.onboardingStatus = status;
    this.cd.markForCheck();
  }

  public isOnboardingInProgress(): boolean {
    const status = this.employee?.attributes?.onboardingStatus;
    return status === OnboardingStatus.WAITING_SUBMIT_FROM_EMPLOYEE
      || status === OnboardingStatus.WAITING_CORRECTION_FROM_EMPLOYEE
      || status === OnboardingStatus.PENDING_VALIDATION;
  }

  public fetchManageList(): void {
    if (this.rolesService.hasRole('PEOPLE_READ', 'PEOPLE_WRITE', 'PEOPLE_PARTIAL_UPDATE')) {
      this.employeesService.getViewManagers(this.employeeId).subscribe(response => {
        this.managerOrganizations = response;

        if (this.managerOrganizations) {
          Object.keys(this.managerOrganizations)
            .forEach((organizationKey: string) => {
              this.managerOrganizations[organizationKey].forEach((manager) => {
                const regex = /\(.+\)/g;
                const positionName = manager.nodeName.match(regex);

                if (positionName) {
                  manager.positionName = positionName[0].replace(/[\(,\)]/g, '');
                  manager.nodeName = manager.nodeName.replace(regex, '');
                }
              });
            });
        }
      });
    }
  }

  public getMenuItems() {
    this.formDescriptorService.hasWorkflowFields(this.cliRegId, 'PEOPLE_EXTEND')
      .pipe(
        tap(hasWorkflowFields => this.isExtendWorkFlowAvailable = hasWorkflowFields),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.menuItems = {};
    this.payPeriodContracts = [];
    this.menuItems$?.unsubscribe();

    this.menuItemsObservable$ = this.employeesService.getOngoingPayPeriodContracts(this.cliRegId, this.contractId);
    this.menuItems$ = this.menuItemsObservable$
      .pipe(
        takeUntil(this.componentDestroyed$),
        tap((payPeriodContracts) => {
          this.setReportDefinitionsAsMenuItems();
          if (payPeriodContracts && payPeriodContracts.length > 0) {
            this.payPeriodContracts = this.payPeriodContractsUtils.formatPayPeriodContractsToDisplayBadges(payPeriodContracts);
            this.payTypesMap = this.payPeriodContractsUtils.groupByPayTypeCalculationType(this.payPeriodContracts);
            this.worstPayPeriodContract = this.payPeriodContractsUtils.findWorstPayPeriodContract(this.payPeriodContracts);

            if (this.isWorstPayrollStatusUpdated()) {
              this.payrollStatusChanged.next(this.worstPayPeriodContract);
            }

            if (this.menuItems$ && this.isWorstPayrollStatusFinal()) {
              this.menuItems$.unsubscribe();
            }

            this.payrollStatus = this.worstPayPeriodContract.status;

          } else {
            this.menuItems$?.unsubscribe();
          }
          if (!this.isDestroyed) {
            this.cd.detectChanges();
          }
        })
      )
      .subscribe();

  }

  // NORMAL first
  public payTypeCalculationTypeComparator(a: KeyValue<string, PayPeriodContract[]>, b: KeyValue<string, PayPeriodContract[]>) {
    return a.key === CalculationType.NORMAL.toString() ? -1 : 1;
  }

  public executePayroll(type: string, payrollId: number) {
    this.menuItems.opened = false;
    let request: Observable<any>;

    switch (type) {
      case 'launch':
        request = this.payrollsService.launch(
          [payrollId],
          this.isForceRetroactivity,
          this.isDebuggingMode,
          this.retroactivityDate
        );
        break;
      case 'done':
        request = this.payrollsService.changeStatus([payrollId], [], [], [], [], []);
        break;
      case 'freeze':
        request = this.payrollsService.changeStatus([], [payrollId], [], [], [], []);
        break;
      case 'unfreeze':
        request = this.payrollsService.changeStatus([], [], [payrollId], [], [], []);
        break;
      case 'lastDone':
        request = this.payrollsService.changeStatus([], [], [], [payrollId], [], []);
        break;
      default:
        break;
    }

    if (request) {
      request.subscribe(() => this.getMenuItems());
    }
  }

  public selectReportDefinition(reportDefinition: ReportDefinition) {
    this.menuItems.opened = false;
    this.selectedReportDefinition = reportDefinition;
    this.hiddenValuesForReportDefinition.set('contractId', this.contractId);
    this.hiddenValuesForReportDefinition.set('employeeId', this.employeeId);
  }

  public reportDefinitionsLoaded(formValuesWrapper: ReportsFormData) {
    this.reportDefinitionsWrapper = formValuesWrapper;
    this.reportDefinitions = formValuesWrapper.data;
    this.setReportDefinitionsAsMenuItems();
    if (!this.isDestroyed) {
      this.cd.detectChanges();
    }
  }

  private setReportDefinitionsAsMenuItems() {
    if (!this.reportDefinitions || !this.menuItems) {
      return;
    }
    this.menuItems.communications = this.getReportDefinitionsAsMenuItemsByTypes(...this.availableReportsType);
  }

  public getReportDefinitionsAsMenuItemsByTypes(...types: string[]) {
    if (!this.reportDefinitions) {
      return [];
    }
    let menuItems = [];
    for (const type of types) {
      menuItems = menuItems.concat(this.reportDefinitions
        .filter((reportDefinition: ReportDefinition) =>
          reportDefinition.reportType === type && (!this.newOnboardingEnabled || reportDefinition.reportName !== 'OnboardingForm')));
    }
    return menuItems;
  }

  public reportDefinitionListener(event: { reportDefinition: ReportDefinition, status: string }) {
    this.selectedReportDefinition = null;
  }

  public selectWorkflow(workflow: BpmnWorkflow) {
    this.menuItems.opened = false;
    this.workflowSubject.next(workflow.key);
  }

  public routeTo(path: string, params?: any, queryParams?: any) {
    this.router.navigate([path, params], queryParams);
  }

  public hasVisibleChild(): boolean {
    let isFound = false;
    for (const item of this.menuItemsElements) {
      if (!item.classList.contains('hide')) {
        return isFound = true;
      }
    }
    return isFound;
  }

  public submitLaunchPayrollModal(id: number): void {
    this.executePayroll('launch', id);
    this.modalService.closeTopmostModal();
  }

  public openLaunchPayrollModal(id: number): void {
    this.currentPayPeriodContractId = id;
    this.menuItems.opened = false;
    this.clearRetroactivity();
    this.modalService.openModalWithId('launchPayrollModal');
  }

  public closeLaunchPayrollModal(): void {
    this.modalService.closeTopmostModal();
  }

  public openDeleteContractModal(): void {
    this.menuItems.opened = false;
    this.modalService.openModalWithId('deleteContractModal');
  }

  public clearRetroactivity(): void {
    this.isForceRetroactivity = false;
    this.isDebuggingMode = false;
    this.retroactivityDate = '';
  }

  public openLastDoneModal(id: number): void {
    this.menuItems.opened = false;
    this.modalService.confirm({
      title: 'FORMS.confirm',
      text: 'PAYROLL_ONGOING.last_done_confirm'
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.executePayroll('lastDone', id);
      }
    });
  }

  public openTestCreationModal(): void {
    this.menuItems.opened = false;
    this.modalService.openModalWithName('HawkTestCreationModalComponent').then((modalRef: any) => {
      modalRef.instance.contractId = this.contractId;
      modalRef.instance.cliRegId = this.cliRegId;
    });
  }

  public navigateToManager(id: number) {
    if (isPresent(id)) {
      this.router.navigate(['employees', 'files', id]);
    }
  }

  public onManagerImageError(manager): void {
    manager.photo = null;
  }

  public openDebugFlatten(contractId: number) {
    this.api.silent.ga20
      .getAndDisplayInWindow(`/contract/${contractId}/flatten?begindate=1970-01-01&enddate=3000-01-01`, 'application/json')
      .subscribe();
  }

  public openDebugCheck(contractId: number) {
    this.api.silent.ga20
      .getAndDisplayInWindow(`/contract/${contractId}/debug-check`, 'text/html')
      .subscribe();
  }

  public openDebugMapPerson(contractId: number) {
    this.api.silent.sdconnect
      .getAndDisplayInWindow(`/admin/sdc3/contract/${contractId}/person`, 'application/json')
      .subscribe();
  }

  public openDebugMapPayroll(contractId: number) {
    this.api.silent.sdconnect
      .getAndDisplayInWindow(`/admin/sdc3/contract/${contractId}/payroll`, 'application/json')
      .subscribe();
  }

  public onUserUpdate(updatedUser: Employee): void {
    this.employee = new Employee(updatedUser);
    this.updatedEmployee.emit(this.employee);
  }

  public onModalOpen(): void {
    this.menuItems.opened = false;
  }

  public forceRefresh(contractId: number, sendOnChange = true): void {
    if (this.forceRefreshed) {
      return;
    }
    this.menuItems.opened = false;
    this.forceRefreshed = true;
    this.api.ga20.post(`/contract/${contractId}/force-refresh?send-on-change=${sendOnChange}`, null)
      .pipe(tap(() => this.toastrService.success('Force refresh triggered, reload employee file in a few seconds.')))
      .subscribe();
  }

  private isWorstPayrollStatusFinal() {
    return ['TODO', 'READY', 'DONE', 'ALERT', 'ERROR', 'FROZEN'].includes(this.worstPayPeriodContract.status);
  }

  private isWorstPayrollStatusUpdated() {
    return this.payrollStatus
      && this.payrollStatus !== this.worstPayPeriodContract.status
      && ['DONE', 'ALERT', 'ERROR'].includes(this.worstPayPeriodContract.status);
  }

  public terminateContract() {
    this.menuItems.opened = false;
    if (this.isTerminatedContract()) {
      this.modalService.confirm({
        'title': 'DIALOG.select_terminated_contract',
        'text': 'DIALOG.select_terminated_contract?',
        'type': 'warning'
      }).then((confirmed: boolean) => {
        if (confirmed) {
          this.routeTo('/employees/leave', this.contract.id);
        }
      });
    } else {
      this.routeTo('/employees/leave', this.contract.id);
    }
  }

  private isTerminatedContract() {
    return moment(this.contract?.enddate).isBefore(DATE_MAX);
  }

  public getManagerInitials(nodeName: string): string {
    return getInitials(nodeName);
  }
}
